import { ClientBookingDataTransferType, ClientBookingDataVehicle } from "@/redux/apis/clientBooking/types";

/**
 * Vehicles bookable with the given transfer type. A transfer type carries
 * `vehicle_exclusions` (vehicle UUIDs); the old sheet removes those from the
 * picker, and we mirror that. No transfer type / no exclusions ⇒ all vehicles.
 */
export const filterVehiclesByTransfer = (
  vehicles: ClientBookingDataVehicle[],
  transfer: ClientBookingDataTransferType | undefined
): ClientBookingDataVehicle[] => {
  if (!transfer || !transfer.vehicle_exclusions?.length) return vehicles;
  const excluded = new Set(transfer.vehicle_exclusions);
  return vehicles.filter((v) => !excluded.has(v.uuid));
};

/** "1 Pax & 1 Bag" / "3 Pax & 2 Bags" — the capacity summary shown in the Trip Details row and Booking Summary. */
export const capacityLabel = (passengers: number, bags: number) =>
  `${passengers} Pax & ${bags} Bag${bags === 1 ? "" : "s"}`;

/**
 * Comma-separated extra names, quantities in brackets ("Baby Seat (2), Child Seat"), or "" when
 * none — shown under the vehicle in Trip Details, the Booking Summary and the confirmation card.
 *
 * The bracket notation is the backend's own: a priced invoice line item is labelled
 * `"<name> (<qty>)"` (JobAnalyzer's ExtraOptionAbstract), so a draft reads the same way as the job
 * it becomes. A quantity of one is left implicit, as it is server-side.
 */
export const extrasLabel = (extras: { name?: string; quantity?: number }[] | undefined) =>
  (extras ?? [])
    .filter((e) => e.name)
    .map((e) => ((e.quantity ?? 1) > 1 ? `${e.name} (${e.quantity})` : e.name))
    .join(", ");

/**
 * Human duration for an hourly/daily booking from its minute count ("2 hours", "1.5 hours", "1 day").
 * Whole-day multiples read as days; everything else as hours. "" when unset (journey bookings).
 */
export const formatDuration = (minutes: number | undefined | null): string => {
  if (!minutes) return "";
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} ${days === 1 ? "day" : "days"}`;
  }
  const hours = minutes / 60;
  const value = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
  return `${value} ${hours === 1 ? "hour" : "hours"}`;
};
